import { type ComponentPropsWithoutRef, type ReactNode } from 'react';
import cx from 'clsx';
import { LottiePlayer, type LottiePlayerProps } from '#components/lottie-player/lottie-player';
import styles from './styles.module.css';

export type IllustrationSize = 'small' | 'medium' | 'big';

export interface IllustrationProps extends Omit<ComponentPropsWithoutRef<'div'>, 'children'> {
  illuPath?: string;
  lottiePath?: string | object;
  lottieSrc?: string;
  size?: IllustrationSize;
  lottieOptions?: LottiePlayerProps;
}

function getIllustrationContent({
  illuPath,
  lottiePath,
  lottieSrc,
  lottieOptions,
}: Pick<IllustrationProps, 'illuPath' | 'lottiePath' | 'lottieSrc' | 'lottieOptions'>): ReactNode {
  if (illuPath) {
    return <img alt="" data-test-q-state-image role="presentation" src={illuPath} />;
  }

  if (lottieSrc) {
    return <LottiePlayer data-test-q-state-lottie src={lottieSrc} {...lottieOptions} />;
  }

  if (typeof lottiePath === 'string') {
    return <LottiePlayer data-test-q-state-lottie path={lottiePath} {...lottieOptions} />;
  }

  if (typeof lottiePath === 'object') {
    return <LottiePlayer animationData={lottiePath} data-test-q-state-lottie {...lottieOptions} />;
  }

  return null;
}

export function Illustration({
  className = '',
  illuPath,
  lottiePath,
  lottieSrc,
  lottieOptions,
  size = 'medium',
  style,
  ...props
}: IllustrationProps): ReactNode {
  const wrapperStyle = lottieOptions?.style
    ? {
        ...style,
        width: lottieOptions.style.width ?? style?.width,
        height: lottieOptions.style.height ?? style?.height,
      }
    : style;

  return (
    <div
      className={cx(
        styles.wrapper,
        styles[`wrapper--${size}`],
        lottieSrc && !className && !lottieOptions?.style && styles['wrapper--lottie'],
        className
      )}
      data-test-q-state
      style={wrapperStyle}
      {...props}
    >
      {getIllustrationContent({ illuPath, lottiePath, lottieSrc, lottieOptions })}
    </div>
  );
}
